import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, Power, Wrench } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const blankWindow = { starts_at: "", ends_at: "", notice: "" };

const when = (value) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const toIso = (value) => (value ? new Date(value).toISOString() : "");

export default function AdminMaintenance() {
  const [data, setData] = useState(null);
  const [windowForm, setWindowForm] = useState(blankWindow);
  const [emergencyNotice, setEmergencyNotice] = useState("");
  const [pendingEmergency, setPendingEmergency] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data: payload } = await api.get("/admin/maintenance");
    setData(payload);
    setEmergencyNotice(payload.emergency.notice || "");
  }, []);

  useEffect(() => {
    load().catch((error) => toast.error(formatApiError(error.response?.data?.detail)));
  }, [load]);

  const schedule = async (event) => {
    event.preventDefault();
    setBusy("schedule");
    try {
      await api.post("/admin/maintenance/windows", {
        starts_at: toIso(windowForm.starts_at),
        ends_at: toIso(windowForm.ends_at),
        notice: windowForm.notice,
      });
      setWindowForm(blankWindow);
      toast.success("Maintenance window scheduled");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const cancel = async (windowId) => {
    if (!window.confirm("Cancel this scheduled maintenance window?")) return;
    setBusy(windowId);
    try {
      await api.delete(`/admin/maintenance/windows/${windowId}`);
      toast.success("Maintenance window cancelled");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const setEmergency = async () => {
    const enabled = !data.emergency.enabled;
    setBusy("emergency");
    try {
      await api.put("/admin/maintenance/emergency", {
        enabled,
        notice: emergencyNotice,
      });
      toast.success(enabled ? "Emergency maintenance is active" : "Emergency maintenance ended");
      setPendingEmergency(false);
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (!data) {
    return (
      <div className="p-10 eyebrow" data-testid="admin-maintenance-loading">
        Loading…
      </div>
    );
  }

  const emergencyActive = data.emergency.enabled;

  return (
    <div
      className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10"
      data-testid="admin-maintenance-page"
    >
      <div className="eyebrow mb-2">System · {data.environment}</div>
      <h1
        className="font-heading text-4xl font-black leading-none tracking-tight md:text-5xl"
        style={{ color: "var(--ink)" }}
      >
        Maintenance
      </h1>
      <p
        className="mt-3 max-w-3xl text-sm"
        style={{ color: "var(--muted)" }}
        data-testid="maintenance-page-description"
      >
        These controls apply only to {data.environment}. Schedule a notice ahead of planned
        downtime or immediately protect employer access during an emergency. Administrator access
        remains open.
      </p>

      <section
        className="mt-10 border p-6"
        style={{
          borderColor: emergencyActive ? "#B45309" : "var(--border)",
          background: "var(--surface)",
        }}
        data-testid="maintenance-emergency-panel"
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="eyebrow mb-2 flex items-center gap-2">
              <AlertTriangle size={13} /> Emergency control
            </div>
            <h2 className="font-heading text-2xl font-bold" style={{ color: "var(--ink)" }}>
              {emergencyActive ? "Employer access is paused" : "Pause employer access now"}
            </h2>
            <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
              Starting emergency maintenance ends active employer sessions. It does not interrupt
              any administrator session.
            </p>
          </div>
          <button
            type="button"
            className={emergencyActive ? "btn-ghost" : "btn-primary"}
            onClick={() => setPendingEmergency(true)}
            disabled={busy === "emergency"}
            data-testid="maintenance-emergency-toggle"
          >
            <Power size={15} />
            {busy === "emergency"
              ? "Saving…"
              : emergencyActive
                ? "End emergency maintenance"
                : "Start emergency maintenance"}
          </button>
        </div>
        <div className="mt-6 max-w-2xl">
          <label className="field-label" htmlFor="maintenance-emergency-notice">
            Employer notice
          </label>
          <textarea
            id="maintenance-emergency-notice"
            rows={3}
            className="input-flat w-full"
            value={emergencyNotice}
            onChange={(event) => setEmergencyNotice(event.target.value)}
            maxLength={280}
            placeholder="We are completing urgent maintenance. Please return shortly."
            data-testid="maintenance-emergency-notice-input"
          />
        </div>
      </section>

      <div className="mt-12 grid gap-10 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <section>
          <div className="eyebrow mb-2 flex items-center gap-2">
            <CalendarClock size={13} /> Planned downtime
          </div>
          <h2 className="font-heading text-2xl font-bold" style={{ color: "var(--ink)" }}>
            Schedule a maintenance window
          </h2>
          <form
            className="mt-5 border p-6 space-y-5"
            style={{ borderColor: "var(--border)" }}
            onSubmit={schedule}
            data-testid="maintenance-schedule-form"
          >
            <div>
              <label className="field-label" htmlFor="maintenance-start">Starts</label>
              <input
                id="maintenance-start"
                type="datetime-local"
                className="input-flat w-full"
                value={windowForm.starts_at}
                onChange={(event) =>
                  setWindowForm({ ...windowForm, starts_at: event.target.value })
                }
                required
                data-testid="maintenance-start-input"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="maintenance-end">Ends</label>
              <input
                id="maintenance-end"
                type="datetime-local"
                className="input-flat w-full"
                value={windowForm.ends_at}
                onChange={(event) => setWindowForm({ ...windowForm, ends_at: event.target.value })}
                required
                data-testid="maintenance-end-input"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="maintenance-notice">Employer notice</label>
              <textarea
                id="maintenance-notice"
                rows={4}
                className="input-flat w-full"
                value={windowForm.notice}
                onChange={(event) => setWindowForm({ ...windowForm, notice: event.target.value })}
                maxLength={280}
                required
                data-testid="maintenance-notice-input"
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={busy === "schedule"}
              data-testid="maintenance-schedule-submit"
            >
              <CalendarClock size={15} />
              {busy === "schedule" ? "Scheduling…" : "Schedule maintenance"}
            </button>
          </form>
        </section>

        <section>
          <div className="eyebrow mb-2 flex items-center gap-2"><Wrench size={13} /> Queue</div>
          <h2 className="font-heading text-2xl font-bold" style={{ color: "var(--ink)" }}>
            Scheduled windows
          </h2>
          <div
            className="mt-5 max-h-[520px] overflow-auto border"
            style={{ borderColor: "var(--border)" }}
            data-testid="maintenance-windows-region"
          >
            <table className="w-full min-w-[640px] text-sm" data-testid="maintenance-windows-table">
              <thead>
                <tr className="border-b text-left eyebrow" style={{ borderColor: "var(--border)" }}>
                  <th className="px-4 py-3">Window</th>
                  <th className="px-4 py-3">Employer notice</th>
                  <th className="px-4 py-3">Scheduled by</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.windows.map((window) => (
                  <tr
                    key={window.id}
                    className="border-b align-top"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`maintenance-window-row-${window.id}`}
                  >
                    <td className="px-4 py-4" style={{ color: "var(--ink)" }}>
                      <span className="block font-medium">{when(window.starts_at)}</span>
                      <span
                        className="mt-1 block text-xs"
                        style={{ color: "var(--muted)" }}
                      >
                        to {when(window.ends_at)}
                      </span>
                    </td>
                    <td className="px-4 py-4" style={{ color: "var(--muted)" }}>
                      {window.notice}
                    </td>
                    <td className="px-4 py-4" style={{ color: "var(--muted)" }}>
                      {window.created_by || "—"}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        type="button"
                        className="btn-ghost !py-2"
                        onClick={() => cancel(window.id)}
                        disabled={busy === window.id}
                        data-testid={`maintenance-cancel-${window.id}`}
                      >
                        {busy === window.id ? "Cancelling…" : "Cancel"}
                      </button>
                    </td>
                  </tr>
                ))}
                {!data.windows.length && (
                  <tr>
                    <td
                      colSpan={4}
                      className="p-10 text-center text-sm"
                      style={{ color: "var(--muted)" }}
                      data-testid="maintenance-windows-empty"
                    >
                      No future maintenance windows are scheduled in this environment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Dialog open={pendingEmergency} onOpenChange={setPendingEmergency}>
        <DialogContent className="sm:max-w-lg" data-testid="maintenance-emergency-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {emergencyActive ? "End emergency maintenance?" : "Start emergency maintenance?"}
            </DialogTitle>
            <DialogDescription>
              {emergencyActive
                ? "Employers may sign in again immediately."
                : "All employer sessions will be ended and employers will see your notice. "
                  + "Administrators stay signed in."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setPendingEmergency(false)}
              data-testid="maintenance-emergency-cancel"
            >
              Keep current state
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={setEmergency}
              disabled={busy === "emergency"}
              data-testid="maintenance-emergency-confirm"
            >
              {emergencyActive ? "End maintenance" : "Start maintenance"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}